import { useEffect, useState } from "react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { fetchFaqs } from "../lib/cmsApi";

const fallbackGroups = [
    {
        title: "Getting started",
        slug: "getting-started",
        items: [
            { q: "What exactly is ApexBrain?", a: "ApexBrain is the unified intelligence layer for modern teams — a single workspace combining Projects, Tasks, Docs, Chat, CRM, Finance, HR and 8 more modules, all wired to the same schema, API and AI Co‑pilot." },
            { q: "Do I need a credit card to try it?", a: "No. Sign In at demo.apexbrain.in for demo account with all Professional features — no credit card required." },
            { q: "How long does setup take?", a: "Most teams are productive within an hour." },

        ],
    },
    {
        title: "Product & modules",
        slug: "product",
        items: [
            { q: "Are all 14 modules included on every plan?", a: "Yes. Every plan unlocks all 14 modules (Projects, Tasks, Sprints, Docs, Wikis, Whiteboards, Chat, CRM, Invoicing, Expenses, HR, Timesheets, Forms/Automations, Dashboards). Limits vary by plan." },
            { q: "Can I disable modules I don't use?", a: "Yes — admins can hide or disable any module per workspace, role, or team. Hidden modules don't consume seats or storage." },
            { q: "Does ApexBrain replace Notion, Slack, Jira, etc.?", a: "Most teams replace 5–15 tools with ApexBrain. We provide first‑party importers, side‑by‑side migration guides, and white‑glove onboarding on Business+ plans." },
            { q: "Can I extend ApexBrain with custom code?", a: "No. ApexBrain is a closed-source platform and we don't provide any API or SDK to extend it." },
        ],
    },
    {
        title: "AI Co‑pilot",
        slug: "ai",
        items: [
            { q: "Which AI model powers Co‑pilot?", a: "Claude Sonnet 4.5 by default. Business and Enterprise plans can bring their own model (Claude Opus, GPT‑5, Gemini, or self‑hosted)." },
            { q: "Does ApexBrain train on my data?", a: "Never. Your data is never used to train any model. Co‑pilot reads only the modules and records you authorize, scoped per role." },
            { q: "Can Co‑pilot reason across modules?", a: "Yes — that's the point. Ask 'why is Q4 pipeline slipping?' and Co‑pilot cites deals, tasks, docs and chat messages from across the entire workspace." },
            { q: "Are AI credits shared across users?", a: "Yes — credits pool at the workspace level. Admins can set per‑user or per‑role limits." },
        ],
    },
    {
        title: "Security & compliance",
        slug: "security",
        items: [
            // { q: "Is ApexBrain SOC 2 compliant?", a: "Yes — SOC 2 Type II audited. Reports available under NDA from your account manager." },
            { q: "Where is my data stored?", a: "EU (Frankfurt), India (Mumbai) or US (Virginia) — your choice. Business plans support custom data residency, Enterprise supports single‑region deployments." },
            { q: "What about GDPR, HIPAA & ISO 27001?", a: "We are compliant with GDPR, HIPAA. We are planning to get ISO 27001 certification soon." },
            { q: "How is data encrypted?", a: "AES‑256 at rest, TLS 1.3 in transit. Enterprise customers can bring their own KMS keys (BYOK)." },
        ],
    },
    {
        title: "Billing & support",
        slug: "billing",
        items: [
            { q: "Can I switch plans anytime?", a: "Yes — upgrade instantly, downgrade at the end of your billing cycle. Yearly plans get a prorated credit on upgrade." },
            { q: "What if I cancel?", a: "You retain full access until the end of your billing period. Your data stays available read‑only for 90 days, then anonymized and deleted." },
            { q: "Do you offer startup discounts?", a: "Yes — We offer free forever for teams under 8 seats." },
            { q: "How fast is support?", a: "Median first response: 4 hours email (Pro), 1 hour priority (Business), 15 min dedicated (Enterprise). Community Discord is 24/7." },
        ],
    },
];

export default function FAQsPage() {
    const [groups, setGroups] = useState(fallbackGroups);

    useEffect(() => {
        fetchFaqs()
            .then((data) => {
                if (data?.groups?.length) setGroups(data.groups);
            })
            .catch(() => {});
    }, []);

    return (
        <div className="pt-28 lg:pt-32" data-testid="faqs-page">
            {/* Heading */}
            <section className="relative overflow-hidden py-16 lg:py-20">
                <div className="absolute inset-0 grid-bg opacity-[0.2] pointer-events-none" />
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-3xl mx-auto px-6 sm:px-8 text-center">
                    <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">
                        $ man apexbrain
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
                        Frequently asked.{" "}
                        <span className="gradient-text">Fully answered.</span>
                    </h1>
                    <p className="mt-5 text-zinc-400 max-w-xl mx-auto font-light text-base">
                        Everything from setup to security. Can't find what
                        you're looking for? Email{" "}
                        <a
                            href="mailto:hello@apexbrain.in"
                            className="text-electric hover:underline font-mono"
                        >
                            hello@apexbrain.in
                        </a>
                        .
                    </p>
                </div>
            </section>

            {/* Anchor nav */}
            <section className="sticky top-[76px] z-20 bg-obsidian-300/85 backdrop-blur border-y border-white/[0.06]">
                <div className="max-w-6xl mx-auto px-6 sm:px-8">
                    <div className="flex items-center gap-1 overflow-x-auto py-3 ticker-mask">
                        {groups.map((g) => (
                            <a
                                key={g.slug}
                                href={`#${g.slug}`}
                                className="shrink-0 font-code text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white px-3 py-1.5 rounded-full hover:bg-white/5 transition"
                                data-testid={`faq-anchor-${g.slug}`}
                            >
                                {g.title}
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* Groups */}
            <section className="relative py-12 lg:py-16">
                <div className="max-w-3xl mx-auto px-6 sm:px-8 space-y-16">
                    {groups.map((g) => (
                        <div
                            key={g.slug}
                            id={g.slug}
                            className="scroll-mt-[150px]"
                            data-testid={`faq-group-${g.slug}`}
                        >
                            <p className="font-code text-xs uppercase tracking-[0.25em] text-amber-glow font-semibold mb-4">
                                // {g.slug}
                            </p>
                            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white mb-6">
                                {g.title}
                            </h2>
                            <Accordion
                                type="single"
                                collapsible
                                className="divide-y divide-white/[0.06] border-y border-white/[0.06]"
                            >
                                {g.items.map((it, i) => (
                                    <AccordionItem
                                        key={i}
                                        value={`${g.slug}-${i}`}
                                        className="border-0"
                                    >
                                        <AccordionTrigger className="text-left py-5 font-display text-base font-semibold text-white hover:no-underline">
                                            {it.q}
                                        </AccordionTrigger>
                                        <AccordionContent className="text-zinc-400 text-sm leading-relaxed font-light pb-5">
                                            {it.a}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
